import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, GripVertical, X, CheckCircle2 } from 'lucide-react';
// Color palette similar to Monday.com
const COLORS = [
    '#229954', // Green
    '#a4d65e', // Light Green
    '#f9b233', // Yellow
    '#ff9800', // Orange
    '#ef5350', // Red
    '#e91e63', // Pink
    '#9c27b0', // Purple
    '#3f51b5', // Indigo
    '#2196f3', // Blue
    '#00bcd4', // Cyan
    '#4db8a8', // Teal
    '#78909c', // Gray
];
// Draggable label item
const SortableLabelItem = ({ label, onEdit, onDelete, onOpenColorPicker, onToggleDone }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id: label.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (_jsxs("div", { ref: setNodeRef, style: style, className: "flex items-center gap-3 p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors", children: [_jsx("button", { ...attributes, ...listeners, className: "cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-200", children: _jsx(GripVertical, { className: "w-4 h-4" }) }), _jsx("div", { className: "w-6 h-6 rounded flex-shrink-0 cursor-pointer hover:opacity-80", style: { backgroundColor: label.color }, onClick: () => onOpenColorPicker(label), title: "Click to change color" }), _jsx("input", { type: "text", value: label.label, onChange: (e) => onEdit({ ...label, label: e.target.value }), className: "flex-1 bg-gray-600 text-white px-2 py-1 rounded text-sm border border-gray-500 focus:outline-none focus:border-blue-400", placeholder: "Label name" }), _jsx("button", { onClick: () => onToggleDone(label.id), className: `p-1 transition-colors ${label.isDone
                    ? 'text-green-400 hover:text-green-300'
                    : 'text-gray-400 hover:text-green-400'}`, title: label.isDone ? 'Mark as not done' : 'Mark as done', children: _jsx(CheckCircle2, { className: "w-4 h-4" }) }), _jsx("button", { onClick: () => onDelete(label.id), className: "p-1 text-gray-400 hover:text-red-400 transition-colors", children: _jsx(Trash2, { className: "w-4 h-4" }) })] }));
};
// Color picker
const ColorPicker = ({ selectedColor, onSelect, onClose }) => {
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50", children: _jsxs("div", { className: "bg-gray-800 rounded-lg p-6 w-80", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsx("h3", { className: "text-white font-medium", children: "Choose Color" }), _jsx("button", { onClick: onClose, className: "text-gray-400 hover:text-white", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsx("div", { className: "grid grid-cols-4 gap-3", children: COLORS.map((color) => (_jsx("button", { onClick: () => {
                            onSelect(color);
                            onClose();
                        }, className: `w-12 h-12 rounded-lg transition-all ${selectedColor === color ? 'ring-2 ring-white scale-110' : ''}`, style: { backgroundColor: color } }, color))) })] }) }));
};
export const StatusColumnEditor = ({ labels, onLabelsChange, isOpen, onClose, columnId, columnName, onColumnNameChange, onDeleteColumn, }) => {
    const [editingLabel, setEditingLabel] = useState(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [isEditingColumnName, setIsEditingColumnName] = useState(false);
    const [editedColumnName, setEditedColumnName] = useState(columnName || '');
    // Update editedColumnName when columnName prop changes
    useEffect(() => {
        setEditedColumnName(columnName || '');
    }, [columnName]);
    const sensors = useSensors(useSensor(PointerSensor, {
        distance: 8,
    }));
    const handleAddLabel = () => {
        const newLabel = {
            id: `label-${Date.now()}`,
            label: 'New Label',
            color: COLORS[0],
        };
        console.log('[Status Label Added] New label created:', {
            id: newLabel.id,
            label: newLabel.label,
            color: newLabel.color,
            totalLabels: labels.length + 1,
        });
        onLabelsChange([...labels, newLabel]);
    };
    const handleDeleteLabel = (id) => {
        const deletedLabel = labels.find((label) => label.id === id);
        console.log('[Status Label Deleted] Label removed:', {
            id,
            label: deletedLabel?.label || 'Unknown',
            color: deletedLabel?.color || 'N/A',
            remainingLabels: labels.length - 1,
        });
        onLabelsChange(labels.filter((label) => label.id !== id));
    };
    const handleEditLabel = (updatedLabel) => {
        const oldLabel = labels.find((label) => label.id === updatedLabel.id);
        if (oldLabel?.color !== updatedLabel.color) {
            console.log('[Status Color Update] Label color changed:', {
                id: updatedLabel.id,
                label: updatedLabel.label,
                oldColor: oldLabel?.color,
                newColor: updatedLabel.color,
            });
        }
        if (oldLabel?.label !== updatedLabel.label) {
            console.log('[Status Label Update] Label text changed:', {
                id: updatedLabel.id,
                oldLabel: oldLabel?.label,
                newLabel: updatedLabel.label,
                color: updatedLabel.color,
            });
        }
        if (oldLabel?.isDone !== updatedLabel.isDone) {
            console.log('[Status Done Toggle] Label done status changed:', {
                id: updatedLabel.id,
                label: updatedLabel.label,
                isDone: updatedLabel.isDone,
            });
        }
        setEditingLabel(updatedLabel);
        onLabelsChange(labels.map((label) => (label.id === updatedLabel.id ? updatedLabel : label)));
    };
    const handleOpenColorPicker = (label) => {
        setEditingLabel(label);
        setShowColorPicker(true);
    };
    const handleToggleDone = (id) => {
        const labelToToggle = labels.find((label) => label.id === id);
        const updatedLabels = labels.map((label) => label.id === id ? { ...label, isDone: !label.isDone } : label);
        console.log('[Status Done Toggle] Label marked as', !labelToToggle?.isDone ? 'done' : 'not done', ':', {
            id,
            label: labelToToggle?.label,
            isDone: !labelToToggle?.isDone,
        });
        onLabelsChange(updatedLabels);
    };
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const activeIndex = labels.findIndex((l) => l.id === active.id);
            const overIndex = labels.findIndex((l) => l.id === over.id);
            const newLabels = Array.from(labels);
            newLabels.splice(activeIndex, 1);
            newLabels.splice(overIndex, 0, labels[activeIndex]);
            const movedLabel = labels[activeIndex];
            console.log('[Status Labels Reordered] Label position changed:', {
                label: movedLabel.label,
                fromIndex: activeIndex,
                toIndex: overIndex,
                newOrder: newLabels.map((l, i) => ({ index: i, label: l.label })),
            });
            onLabelsChange(newLabels);
        }
        setActiveId(null);
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50", onClick: onClose, children: [_jsxs("div", { className: "bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsx("div", { className: "flex-1", children: isEditingColumnName ? (_jsx("div", { className: "flex items-center gap-2", children: _jsx("input", { type: "text", value: editedColumnName, onChange: (e) => setEditedColumnName(e.target.value), className: "px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-400", autoFocus: true, onBlur: () => {
                                            if (editedColumnName.trim() && columnId && onColumnNameChange) {
                                                console.log('[Status Column Renamed]', {
                                                    columnId,
                                                    oldName: columnName,
                                                    newName: editedColumnName.trim(),
                                                });
                                                onColumnNameChange(columnId, editedColumnName.trim());
                                            }
                                            setIsEditingColumnName(false);
                                        }, onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                if (editedColumnName.trim() && columnId && onColumnNameChange) {
                                                    console.log('[Status Column Renamed]', {
                                                        columnId,
                                                        oldName: columnName,
                                                        newName: editedColumnName.trim(),
                                                    });
                                                    onColumnNameChange(columnId, editedColumnName.trim());
                                                }
                                                setIsEditingColumnName(false);
                                            }
                                            else if (e.key === 'Escape') {
                                                setEditedColumnName(columnName || '');
                                                setIsEditingColumnName(false);
                                            }
                                        } }) })) : (_jsx("h2", { className: "text-xl font-semibold text-white cursor-pointer hover:opacity-80 transition-opacity", onClick: () => setIsEditingColumnName(true), title: "Click to edit column name", children: columnName || 'Edit Labels' })) }), _jsxs("div", { className: "flex items-center gap-2 ml-4", children: [onDeleteColumn && columnId && (_jsx("button", { onClick: () => {
                                            if (confirm(`Delete column "${columnName}"?`)) {
                                                onDeleteColumn(columnId);
                                                onClose();
                                            }
                                        }, className: "p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors", title: "Delete column", children: _jsx(Trash2, { className: "w-5 h-5" }) })), _jsx("button", { onClick: onClose, className: "p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors", children: _jsx(X, { className: "w-5 h-5" }) })] })] }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragEnd: handleDragEnd, children: _jsxs(SortableContext, { items: labels.map((l) => l.id), strategy: verticalListSortingStrategy, children: [_jsx("div", { className: "space-y-3 mb-6", children: labels.map((label) => (_jsx(SortableLabelItem, { label: label, onEdit: handleEditLabel, onDelete: handleDeleteLabel, onOpenColorPicker: handleOpenColorPicker, onToggleDone: handleToggleDone }, label.id))) }), _jsx(DragOverlay, { children: activeId
                                        ? labels.find((l) => l.id === activeId) && (_jsxs("div", { className: "bg-gray-700 p-3 rounded-lg shadow-lg flex items-center gap-3", children: [_jsx("div", { className: "w-6 h-6 rounded", style: {
                                                        backgroundColor: labels.find((l) => l.id === activeId)
                                                            ?.color,
                                                    } }), _jsx("span", { className: "text-white", children: labels.find((l) => l.id === activeId)?.label })] }))
                                        : null })] }) }), _jsxs("button", { onClick: handleAddLabel, className: "w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors mb-4", children: [_jsx(Plus, { className: "w-4 h-4" }), "Add Label"] }), _jsx("button", { onClick: onClose, className: "w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors", children: "Done" })] }), showColorPicker && editingLabel && (_jsx(ColorPicker, { selectedColor: editingLabel.color, onSelect: (color) => {
                    handleEditLabel({ ...editingLabel, color });
                }, onClose: () => setShowColorPicker(false) }))] }));
};
